// AddressBook — list / add / edit / delete saved shipping addresses.
// Used by the Profile "Addresses" tab AND embedded in checkout.

import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MapPin, Star, X, Save, Check } from "lucide-react";
import { api } from "../lib/api";
import { AddressFields } from "./ProfileFields";

const EMPTY = {
  label: "Home", name: "", line1: "", line2: "", city: "", state: "",
  country: "", postal_code: "", phone: "", phone_cc: "", is_default: false,
};

// Normalizes between API shape (zip, postal_code) and form shape.
const apiToForm = (a) => ({ ...a, postal_code: a.postal_code || a.zip || "" });
const formToApi = (a) => ({
  label: a.label || "Home",
  name: a.name || "",
  line1: a.line1 || "",
  line2: a.line2 || "",
  city: a.city || "",
  state: a.state || "",
  country: a.country || "",
  zip: a.postal_code || a.zip || "",
  phone: a.phone || "",
  phone_cc: a.phone_cc || "",
  is_default: !!a.is_default,
});

export default function AddressBook({ onSelect, selectedId, compact = false }) {
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null); // addr_id or "new"
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/buyer/addresses");
      setAddresses(data || []);
    } catch { /* swallow */ }
  };

  useEffect(() => { load(); }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2000); };

  const startNew = () => {
    setDraft({ ...EMPTY, is_default: addresses.length === 0 });
    setEditing("new");
  };

  const startEdit = (a) => {
    setDraft(apiToForm(a));
    setEditing(a.addr_id);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = formToApi(draft);
      if (editing === "new") await api.post("/buyer/addresses", payload);
      else await api.put(`/buyer/addresses/${editing}`, payload);
      setEditing(null); setDraft(EMPTY);
      flash("Saved");
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.label || a.line1}"?`)) return;
    try { await api.delete(`/buyer/addresses/${a.addr_id}`); load(); flash("Deleted"); }
    catch (e) { flash(e?.response?.data?.detail || "Delete failed"); }
  };

  const setDefault = async (a) => {
    try {
      await api.post(`/buyer/addresses/${a.addr_id}/default`);
      flash("Marked default");
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3" data-testid="address-book">
      {msg && (
        <div className="text-xs text-[#D4FF00] font-bold uppercase tracking-widest">{msg}</div>
      )}

      {addresses.length === 0 && editing === null && (
        <div className="card p-5 text-center text-sm text-[#A1A1AA]" data-testid="address-book-empty">
          No saved addresses yet. Add one below.
        </div>
      )}

      {addresses.map(a => {
        const isPicked = onSelect && selectedId === a.addr_id;
        const isEditing = editing === a.addr_id;
        if (isEditing) {
          return (
            <AddressEditor key={a.addr_id} draft={draft} setDraft={setDraft}
              onSave={save} onCancel={() => { setEditing(null); setDraft(EMPTY); }}
              busy={busy}/>
          );
        }
        return (
          <div key={a.addr_id}
            data-testid={`address-card-${a.addr_id}`}
            className={`card p-4 flex items-start gap-3 transition cursor-pointer ${
              isPicked ? "border-[#D4FF00]" :
              a.is_default ? "border-[#00F0FF]/30" : ""
            }`}
            onClick={onSelect ? () => onSelect(a) : undefined}
            role={onSelect ? "button" : undefined}>
            {onSelect && (
              <div className={`w-5 h-5 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center ${
                isPicked ? "border-[#D4FF00] bg-[#D4FF00]" : "border-[#3D2845]"
              }`}>
                {isPicked && <Check size={12} className="text-black"/>}
              </div>
            )}
            <MapPin size={16} className="text-[#A1A1AA] mt-1 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm">{a.label || "Address"}</span>
                {a.is_default && (
                  <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#D4FF00]/15 text-[#D4FF00] flex items-center gap-1"
                    data-testid={`address-default-${a.addr_id}`}>
                    <Star size={9} className="fill-[#D4FF00]"/> Default
                  </span>
                )}
              </div>
              <div className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">
                {a.name && <>{a.name}<br/></>}
                {a.line1}{a.line2 && `, ${a.line2}`}<br/>
                {[a.city, a.state, a.zip].filter(Boolean).join(", ")}
                {a.country && <> · {a.country}</>}
                {a.phone && <><br/>📞 {a.phone_cc ? `+${a.phone_cc} ` : ""}{a.phone}</>}
              </div>
            </div>
            {!compact && (
              <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {!a.is_default && (
                  <button onClick={() => setDefault(a)} title="Make default"
                    data-testid={`address-set-default-${a.addr_id}`}
                    className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
                    <Star size={12}/>
                  </button>
                )}
                <button onClick={() => startEdit(a)} title="Edit"
                  data-testid={`address-edit-${a.addr_id}`}
                  className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center">
                  <Pencil size={12}/>
                </button>
                <button onClick={() => remove(a)} title="Delete"
                  data-testid={`address-delete-${a.addr_id}`}
                  className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
                  <Trash2 size={12}/>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {editing === "new" && (
        <AddressEditor draft={draft} setDraft={setDraft}
          onSave={save} onCancel={() => { setEditing(null); setDraft(EMPTY); }}
          busy={busy}/>
      )}

      {editing === null && (
        <button onClick={startNew}
          data-testid="address-add-btn"
          className="btn-secondary w-full">
          <Plus size={14} className="inline mr-1"/> Add new address
        </button>
      )}
    </div>
  );
}

function AddressEditor({ draft, setDraft, onSave, onCancel, busy }) {
  return (
    <div className="card p-4 space-y-3 border-[#D4FF00]/40" data-testid="address-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1 block">Label</label>
          <input className="input-dark" placeholder="Home, Office, …"
            value={draft.label}
            onChange={e => setDraft({ ...draft, label: e.target.value })}
            data-testid="address-input-label"/>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1 block">Recipient name</label>
          <input className="input-dark" placeholder="Full name"
            value={draft.name}
            onChange={e => setDraft({ ...draft, name: e.target.value })}
            data-testid="address-input-name"/>
        </div>
      </div>
      <AddressFields value={draft} onChange={setDraft} testid="address-input"/>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1 block">Phone</label>
          <input className="input-dark" placeholder="9999999999"
            value={draft.phone}
            onChange={e => setDraft({ ...draft, phone: e.target.value })}
            data-testid="address-input-phone"/>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-7"
          data-testid="address-input-default-wrap">
          <input type="checkbox" checked={!!draft.is_default}
            onChange={e => setDraft({ ...draft, is_default: e.target.checked })}
            data-testid="address-input-default"/>
          Make this my default address
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={onSave} disabled={busy || !draft.line1 || !draft.country}
          data-testid="address-save-btn"
          className="btn-primary flex-1">
          <Save size={14} className="inline mr-1"/> {busy ? "Saving…" : "Save address"}
        </button>
        <button onClick={onCancel} className="btn-secondary"
          data-testid="address-cancel-btn">
          <X size={14} className="inline mr-1"/> Cancel
        </button>
      </div>
    </div>
  );
}
